import { useState, useRef, useEffect } from 'react'

/**
 * SearchableSelect
 *
 * Props:
 * - options: Array de objetos { id, codigo, name }
 * - value:     Objeto { id, codigo, name } o null si no hay selección
 * - onChange:  (nuevoObjeto) => void  // callback cuando se selecciona
 * - placeholder: string
 * - className?: string // clases adicionales para el contenedor
 */
const SearchableSelect = ({
  options,
  value,
  onChange,
  placeholder = 'Selecciona o busca...',
  className = ''
}) => {
  const [inputValue, setInputValue] = useState('') // Texto que va en el input
  const [showDropdown, setShowDropdown] = useState(false)
  const [filteredOptions, setFilteredOptions] = useState(options || [])

  // Ref para detectar clics fuera del componente y cerrar el dropdown
  const containerRef = useRef(null)

  // 1) Cuando cambian las opciones totales (por ejemplo al cargar la primera vez),
  //    actualizamos el filtro y, si value existe, ponemos inputValue al label de value.
  useEffect(() => {
    setFilteredOptions(options)
    if (value) {
      setInputValue(`${value.codigo} - ${value.name}`)
    }
  }, [options, value])

  // 2) Cuando el usuario escribe en el input, filtramos
  const handleInputChange = e => {
    const texto = e.target.value
    setInputValue(texto)

    // Filtrar las opciones que contengan texto en codigo o name (case-insensitive)
    const textoMinus = texto.toLowerCase().trim()
    const newFiltered = options.filter(opt => {
      const label = `${opt.codigo} ${opt.name}`.toLowerCase()
      return label.includes(textoMinus)
    })
    setFilteredOptions(newFiltered)
    setShowDropdown(true)
  }

  // 3) Cuando el usuario hace clic en una opción
  const handleOptionClick = opt => {
    setInputValue(`${opt.codigo} - ${opt.name}`)
    setShowDropdown(false)
    onChange(opt)
  }

  // 4) Cerrar dropdown si se hace clic fuera
  useEffect(() => {
    const handleClickOutside = event => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <input
        type='text'
        className='
          w-full
          border border-gray-300
          rounded-full
          px-2 py-1
          text-sm
          font-sans
          focus:outline-none
          focus:border-blue-800
          outline-none
        '
        placeholder={placeholder}
        value={inputValue}
        onChange={handleInputChange}
        onFocus={() => {
          // Al hacer foco en el input, mostramos el dropdown con todas (o filtradas)
          setFilteredOptions(options)
          setShowDropdown(true)
        }}
      />

      {showDropdown && filteredOptions.length > 0 && (
        <ul
          className='
            absolute left-0 right-0 mt-1
            bg-white border border-gray-300
            rounded-lg shadow-md
            max-h-48 overflow-y-auto
            z-50
          '
        >
          {filteredOptions.map(opt => (
            <li
              key={opt.id}
              onClick={() => handleOptionClick(opt)}
              className='
                px-2 py-1
                hover:bg-gray-100
                cursor-pointer
                text-sm
                font-sans
              '
            >
              {opt.codigo} - {opt.name}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default SearchableSelect
